import React, { useRef, useCallback, useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const VehicleWarrantyPeriodTable = ({ data, loading, onPage, lazyParams, totalCount, onEdit }) => {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);
    const columnsRef = useRef(null);

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0); // 0: none, 1: asc, -1: desc

    const getColumnsCallback = useCallback((el) => {
        if (el && el.getColumns && !columnsRef.current) {
            columnsRef.current = el.getColumns();
        }
        dataTableRef.current = el;
    }, []);

    const sortedData = useMemo(() => {
        if (!data || !sortField || sortOrder === 0) {
            return data;
        }

        const sorted = [...data].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.includes('Date') || sortField === 'sellDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });

        return sorted;
    }, [data, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('vehicleAdditionalWarrantyPeriod.edit')}></i>
                <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        return rowData.status ? th('common.active') : th('common.inactive');
    };

    const addWarrDetailsBodyTemplate = (rowData) => {
        return createTruncatedTextForTable(rowData.addWarrDetails, 50);
    };

    const endUserBodyTemplate = (rowData) => {
        return createTruncatedTextForTable(rowData.endUser, 50);
    };

    const bidBodyTemplate = (rowData) => {
        return rowData.bid ? th('common.yes') : th('common.no');
    };

    const isActiveBodyTemplate = (rowData) => {
        return rowData.isActive ? th('common.active') : th('common.inactive');
    };

    const isLargeDataLoading = loading && lazyParams?.rows >= 100;

    return (
        <div className="relative">
            {isLargeDataLoading && (
                <div className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center bg-white bg-opacity-70 z-5" style={{ minHeight: '400px' }}>
                    <div className="flex flex-column align-items-center">
                        <ProgressSpinner style={{ width: '60px', height: '60px' }} />
                        <p className="mt-3 text-xl font-semibold text-color-secondary">
                            {lazyParams?.rows >= 250 ? th('vehicleAdditionalWarrantyPeriod.loadingLargeDataset', { count: lazyParams.rows }) : th('vehicleAdditionalWarrantyPeriod.loadingData')}
                        </p>
                        <p className="text-sm text-color-secondary">{th('vehicleAdditionalWarrantyPeriod.pleaseWait')}</p>
                    </div>
                </div>
            )}

            <DataTable
                value={sortedData}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 10}
                onPage={onPage}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                dataKey="id"
                loading={loading && !isLargeDataLoading}
                filterDisplay="menu"
                emptyMessage={th('vehicleAdditionalWarrantyPeriod.emptyMessage')}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 250]}
                tableStyle={{ minWidth: '60rem' }}
                ref={getColumnsCallback}
                sortMode="single"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                loadingIcon="pi pi-spin pi-spinner"
            >
                <Column body={actionBodyTemplate} style={{ width: '10%' }} align="center" />
                <Column sortable field="chassisNo" header={th('vehicleAdditionalWarrantyPeriod.columns.chassisNo')} />
                <Column sortable field="carItemCode" header={th('vehicleAdditionalWarrantyPeriod.columns.carItemCode')} />
                <Column sortable field="vehicleItemCode" header={th('vehicleAdditionalWarrantyPeriod.columns.vehicleItemCode')} />
                <Column sortable field="modelYear" header={th('vehicleAdditionalWarrantyPeriod.columns.modelYear')} />
                <Column sortable field="addWarrantyPeriod" header={th('vehicleAdditionalWarrantyPeriod.columns.addWarrantyPeriod')} />
                <Column sortable field="addWarrantyKmInfo" header={th('vehicleAdditionalWarrantyPeriod.columns.addWarrantyKmInfo')} />
                <Column sortable field="addWarrDetails" header={th('vehicleAdditionalWarrantyPeriod.columns.addWarrDetails')} body={addWarrDetailsBodyTemplate} style={{ maxWidth: '300px' }} />
                <Column sortable field="carManuDate" header={th('vehicleAdditionalWarrantyPeriod.columns.carManuDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'carManuDate')} />
                <Column sortable field="curWarrantyPeriod" header={th('vehicleAdditionalWarrantyPeriod.columns.curWarrantyPeriod')} />
                <Column sortable field="curWarrantyKmInfo" header={th('vehicleAdditionalWarrantyPeriod.columns.curWarrantyKmInfo')} />
                <Column sortable field="odometerKmInfo" header={th('vehicleAdditionalWarrantyPeriod.columns.odometerKmInfo')} />
                <Column sortable field="brand" header={th('vehicleAdditionalWarrantyPeriod.columns.brand')} />
                <Column sortable field="brandModel" header={th('vehicleAdditionalWarrantyPeriod.columns.model')} />
                <Column sortable field="productGroup" header={th('vehicleAdditionalWarrantyPeriod.columns.productGroup')} />
                <Column sortable field="descTr" header={th('vehicleAdditionalWarrantyPeriod.columns.description')} />
                <Column sortable field="serial" header={th('vehicleAdditionalWarrantyPeriod.columns.serial')} />
                <Column sortable field="bid" header={th('vehicleAdditionalWarrantyPeriod.columns.bid')} body={bidBodyTemplate} />
                <Column sortable field="bidWarrantyPeriod" header={th('vehicleAdditionalWarrantyPeriod.columns.bidWarrantyPeriod')} />
                <Column sortable field="bidWarrantyKmInfo" header={th('vehicleAdditionalWarrantyPeriod.columns.bidWarrantyKmInfo')} />
                <Column sortable field="sellDate" header={th('vehicleAdditionalWarrantyPeriod.columns.sellDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'sellDate')} />
                <Column sortable field="endUser" header={th('vehicleAdditionalWarrantyPeriod.columns.endUser')} body={endUserBodyTemplate} style={{ maxWidth: '300px' }} />
                <Column sortable field="dealerName" header={th('vehicleAdditionalWarrantyPeriod.columns.dealerName')} body={(rowData) => createTruncatedTextForTable(rowData.dealerName, 40)} />
                <Column sortable field="dealerId" header={th('vehicleAdditionalWarrantyPeriod.columns.dealerId')} />
                <Column sortable field="startDate" header={th('vehicleAdditionalWarrantyPeriod.columns.startDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'startDate')} />
                <Column sortable field="endDate" header={th('vehicleAdditionalWarrantyPeriod.columns.endDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'endDate')} />
                <Column sortable field="warrEndDate" header={th('vehicleAdditionalWarrantyPeriod.columns.warrEndDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'warrEndDate')} />
                <Column sortable field="warrEndKm" header={th('vehicleAdditionalWarrantyPeriod.columns.warrEndKm')} />
                <Column sortable field="maxKm" header={th('vehicleAdditionalWarrantyPeriod.columns.maxKm')} />
                <Column sortable field="returnCount" header={th('vehicleAdditionalWarrantyPeriod.columns.returnCount')} />
                <Column sortable field="status" header={th('vehicleAdditionalWarrantyPeriod.columns.status')} body={statusBodyTemplate} />
                <Column sortable field="createdName" header={th('vehicleAdditionalWarrantyPeriod.columns.createdName')} />
                <Column sortable field="createdDate" header={th('vehicleAdditionalWarrantyPeriod.columns.createdDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'createdDate')} />
                <Column sortable field="modifiedDate" header={th('vehicleAdditionalWarrantyPeriod.columns.modifiedDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'modifiedDate')} />
                <Column sortable={false} header={th('vehicleAdditionalWarrantyPeriod.columns.file')} exportable={false} />
            </DataTable>
        </div>
    );
};

export default VehicleWarrantyPeriodTable;
